import clsx from 'clsx';
import type { CSSProperties, ElementType } from 'react';

import styles from './SectionHeader.module.scss';

export interface SectionHeaderProps {
  /** Header text - supports <br> for line breaks */
  text?: string;
  /** Text alignment */
  textAlign?: 'left' | 'center' | 'right';
  /** Text color */
  color?: string;
  /** Font size on mobile (px) */
  sizeMobile?: number;
  /** Font size on desktop (px) */
  sizeDesktop?: number;
  /** Disable bottom margin */
  disableGutters?: boolean;
  /** HTML element to render as */
  as?: ElementType;
  /** Optional className */
  className?: string;
}

/**
 * SectionHeader component
 *
 * Displays a styled section heading with configurable sizes and colors.
 * Supports line breaks via `<br>` in the text.
 *
 * @example
 * <SectionHeader
 *   text="Featured<br>Products"
 *   textAlign="center"
 *   sizeDesktop={50}
 *   sizeMobile={40}
 * />
 */
export function SectionHeader({
  text = '',
  textAlign = 'left',
  color,
  sizeMobile = 34,
  sizeDesktop = 40,
  disableGutters = false,
  as: Component = 'h3',
  className,
}: SectionHeaderProps) {
  if (!text) {
    return null;
  }

  // Parse text for <br> tags
  const textLines = text.split('<br>').map((line, lineIndex, arr) => {
    const elements = [
      <span key={`${line}-${lineIndex}`} data-testid={`SectionHeaderLine-${lineIndex}`}>
        {line}
      </span>,
    ];

    if (lineIndex < arr.length - 1) {
      elements.push(<br key={`br-${lineIndex}`} />);
    }

    return elements;
  });

  const style: CSSProperties & Record<string, string> = {
    '--size-mobile': `${sizeMobile}px`,
    '--size-desktop': `${sizeDesktop}px`,
  };

  if (color) {
    style['--header-color'] = color;
  }

  return (
    <Component
      className={clsx(
        styles.header,
        styles[`align${textAlign.charAt(0).toUpperCase()}${textAlign.slice(1)}`],
        disableGutters ? styles.noGutters : styles.withGutters,
        className
      )}
      style={style}
      data-testid="SectionHeader"
    >
      {textLines}
    </Component>
  );
}

SectionHeader.displayName = 'SectionHeader';
